export const isValidIanaTimezone = (timezone?: string | null): timezone is string => {
  const value = String(timezone || '').trim();
  if (!value) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const TENANT_TIMEZONE_STORAGE_PREFIX = 'tenantTimezoneOverride:';

const sanitizeTenantIdForStorage = (value?: string | null): string => {
  const raw = String(value || '').trim();
  if (!raw || raw.toLowerCase() === 'all') return '';
  return raw.replace(/[^a-zA-Z0-9_.:-]/g, '').slice(0, 64);
};

const getActiveTenantIdFromStorage = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const active = sanitizeTenantIdForStorage(window.localStorage?.getItem('activeTenantId'));
    if (active) return active;
    return sanitizeTenantIdForStorage(window.localStorage?.getItem('selectedAccountId'));
  } catch {
    return '';
  }
};

const buildTenantTimezoneStorageKeys = (tenantId?: string | null): string[] => {
  const keys: string[] = [];
  const explicitTenantId = sanitizeTenantIdForStorage(tenantId);
  const activeTenantId = explicitTenantId || getActiveTenantIdFromStorage();
  if (activeTenantId) {
    keys.push(`${TENANT_TIMEZONE_STORAGE_PREFIX}${activeTenantId}`);
  }
  keys.push(`${TENANT_TIMEZONE_STORAGE_PREFIX}default`);
  return keys;
};

export const getStoredTenantTimezone = (tenantId?: string | null): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const keys = buildTenantTimezoneStorageKeys(tenantId);
    for (const key of keys) {
      const value = String(window.localStorage?.getItem(key) || '').trim();
      if (isValidIanaTimezone(value)) return value;
    }
  } catch {
    return null;
  }
  return null;
};

export const setStoredTenantTimezone = (
  timezone?: string | null,
  tenantId?: string | null
): void => {
  if (typeof window === 'undefined') return;
  const normalizedTimezone = String(timezone || '').trim();
  const validTimezone = isValidIanaTimezone(normalizedTimezone) ? normalizedTimezone : '';
  const explicitTenantId = sanitizeTenantIdForStorage(tenantId);
  const activeTenantId = explicitTenantId || getActiveTenantIdFromStorage();
  const scopedKey = activeTenantId ? `${TENANT_TIMEZONE_STORAGE_PREFIX}${activeTenantId}` : '';
  const defaultKey = `${TENANT_TIMEZONE_STORAGE_PREFIX}default`;
  try {
    if (scopedKey) {
      if (validTimezone) window.localStorage?.setItem(scopedKey, validTimezone);
      else window.localStorage?.removeItem(scopedKey);
    }
    if (validTimezone) window.localStorage?.setItem(defaultKey, validTimezone);
    else window.localStorage?.removeItem(defaultKey);
  } catch {
    // Ignore localStorage write issues.
  }
};

export const getTenantTimezoneFromPreferencesData = (
  preferencesData: any,
  tenantId?: string | null
): string | null => {
  const raw = String((preferencesData as any)?.preferences?.companyProfile?.timezone || '').trim();
  if (isValidIanaTimezone(raw)) return raw;
  return getStoredTenantTimezone(tenantId);
};

export const withTimezone = (
  options: Intl.DateTimeFormatOptions,
  timezone?: string | null
): Intl.DateTimeFormatOptions => (timezone ? { ...options, timeZone: timezone } : options);

const toDate = (value: Date | string | number | null | undefined): Date | null => {
  const candidate = value instanceof Date ? value : new Date(value as any);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

type DateParts = {
  year: number;
  month: number;
  day: number;
};

const getDatePartsInTimezone = (
  value: Date | string | number | null | undefined,
  timezone?: string | null
): DateParts | null => {
  const date = toDate(value);
  if (!date) return null;

  const formatter = new Intl.DateTimeFormat(
    'en-US',
    withTimezone(
      {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
      timezone
    )
  );

  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  return { year, month, day };
};

const toDaySerial = (parts: DateParts): number =>
  Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / (24 * 60 * 60 * 1000));

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const getDateKeyInTimezone = (
  value: Date | string | number | null | undefined,
  timezone?: string | null
): string => {
  const parts = getDatePartsInTimezone(value, timezone);
  if (!parts) return '';
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

export const isSameDayInTimezone = (
  a: Date | string | number | null | undefined,
  b: Date | string | number | null | undefined,
  timezone?: string | null
): boolean => {
  const aKey = getDateKeyInTimezone(a, timezone);
  const bKey = getDateKeyInTimezone(b, timezone);
  return Boolean(aKey && bKey && aKey === bKey);
};

type TimeParts = {
  hour: number;
  minute: number;
};

export const getTimePartsInTimezone = (
  value: Date | string | number | null | undefined,
  timezone?: string | null
): TimeParts | null => {
  const date = toDate(value);
  if (!date) return null;

  const formatter = new Intl.DateTimeFormat(
    'en-US',
    withTimezone(
      {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      },
      timezone
    )
  );

  const parts = formatter.formatToParts(date);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return null;
  }

  return { hour, minute };
};

export const getRelativeDayDifferenceInTimezone = (
  value: Date | string | number | null | undefined,
  reference: Date | string | number = new Date(),
  timezone?: string | null
): number | null => {
  const valueParts = getDatePartsInTimezone(value, timezone);
  const referenceParts = getDatePartsInTimezone(reference, timezone);
  if (!valueParts || !referenceParts) return null;
  return toDaySerial(valueParts) - toDaySerial(referenceParts);
};

export const toLocaleDateStringInTimezone = (
  value: Date | string | number | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
  timezone?: string | null
): string => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString(locale, withTimezone(options, timezone));
};

export const toLocaleTimeStringInTimezone = (
  value: Date | string | number | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
  timezone?: string | null
): string => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString(locale, withTimezone(options, timezone));
};

export const toLocaleStringInTimezone = (
  value: Date | string | number | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
  timezone?: string | null
): string => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleString(locale, withTimezone(options, timezone));
};
